import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import {
  PlpPurchaseState,
  NativePlpPurchaseState,
  artworkForOffer,
  guaranteeForOffer,
} from "@theme/plp-purchase-model";

import {
  normalizeOffers,
  offerVariants,
  optionControls,
  matchVariant,
  initialSelections,
  allocationFor,
  stockState,
} from "@theme/product-purchase-model";

const nativeHelpers = {
  normalizeOffers,
  offerVariants,
  optionControls,
  matchVariant,
  initialSelections,
  allocationFor,
  stockState,
};

const escapeHTML = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        char
      ],
  );
const readJSON = (element) => JSON.parse(element?.textContent || "{}");
const text = (template, values) =>
  Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`__${key}__`, String(value)),
    template || "",
  );

/** @extends Component<{dialog: HTMLDialogElement}> */
class PlpPlanDialog extends Component {
  requiredRefs = ["dialog"];

  connectedCallback() {
    super.connectedCallback();
    this.bind();
  }
  updatedCallback() {
    this.closeDialog();
    super.updatedCallback();
    this.bind();
  }
  bind() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.refs.dialog.addEventListener(
      "cancel",
      (event) => {
        event.preventDefault();
        this.closeDialog();
      },
      { signal },
    );
    this.refs.dialog.addEventListener(
      "click",
      (event) => {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest("[data-plp-close]")) this.closeDialog();
        if (event.target === this.refs.dialog) {
          const rect = this.refs.dialog.getBoundingClientRect();
          if (
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom
          )
            this.closeDialog();
        }
      },
      { signal },
    );
    this.refs.dialog.addEventListener("close", () => this.restore(), {
      signal,
    });
  }
  /** @param {HTMLElement} trigger - Button receiving focus after closing. */
  showDialog(trigger) {
    if (this.refs.dialog.open) return;
    for (const other of document.querySelectorAll("plp-plan-dialog")) {
      if (other !== this) other.closeDialog?.();
    }
    this.trigger = trigger;
    this.previousOverflow = document.body.style.overflow;
    this.refs.dialog.showModal();
    document.body.style.overflow = "hidden";
    this.locked = true;
    this.closest("plp-quick-shop")
      ?.querySelectorAll("[data-plp-open]")
      .forEach((button) => button.setAttribute("aria-expanded", "true"));
  }
  closeDialog() {
    if (this.refs.dialog?.open) this.refs.dialog.close();
    this.restore();
  }
  restore() {
    if (!this.locked) return;
    document.body.style.overflow = this.previousOverflow;
    this.locked = false;
    this.closest("plp-quick-shop")
      ?.querySelectorAll("[data-plp-open]")
      .forEach((button) => button.setAttribute("aria-expanded", "false"));
    const trigger = this.trigger?.isConnected
      ? this.trigger
      : this.closest("plp-quick-shop")?.querySelector("[data-plp-open]");
    trigger?.focus({ preventScroll: true });
  }
  disconnectedCallback() {
    this.closeDialog();
    this.abort?.abort();
    super.disconnectedCallback();
  }
}

/** Native selection controller. The existing product-form component owns cart requests. */
class PlpQuickShop extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }
  updatedCallback() {
    this.cleanup();
    super.updatedCallback();
    this.initialize();
  }
  initialize() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.enhanced = false;
    try {
      this.product = readJSON(this.querySelector("[data-plp-product-data]"));
      this.config = readJSON(this.querySelector("[data-plp-config]"));
      this.planOptions = readJSON(
        this.querySelector("[data-plp-plan-options]"),
      );
      const nativeSource = this.querySelector("[data-plp-native-data]");
      this.nativeData = nativeSource ? readJSON(nativeSource) : null;
      if (this.nativeData) this.product = this.nativeData.product;
      this.state = this.nativeData
        ? new NativePlpPurchaseState(
            this.nativeData,
            this.config,
            nativeHelpers,
          )
        : new PlpPurchaseState(this.product, this.config);
      this.strings = this.config.strings;
      this.form = this.querySelector("form");
      if (!this.form || !this.state.selected)
        throw new Error("No native offer is available");
      this.form.purchaseController = this;
      // Newly inserted cards connect before nested custom elements upgrade.
      this.querySelector("plp-plan-dialog")?.updatedCallback?.();
      this.pendingObserver?.disconnect();
      this.pendingObserver = new MutationObserver(() => {
        if (this.form.dataset.purchasePending === "true") {
          this.put(".add-to-cart-text__content", this.strings.loading);
        } else if (this.selectionAvailable()) {
          this.put(
            ".add-to-cart-text__content",
            `${this.strings.add} | ${this.money(this.state.price())}`,
          );
        }
      });
      this.pendingObserver.observe(this.form, {
        attributes: true,
        attributeFilter: ["aria-busy"],
      });
      this.addEventListener("change", (event) => this.handleChange(event), {
        signal,
      });
      this.addEventListener("click", (event) => this.handleClick(event), {
        signal,
      });
      document.addEventListener(
        "click",
        (event) => {
          for (const select of this.querySelectorAll(
            "details[data-plp-select][open]",
          )) {
            if (!select.contains(event.target)) select.open = false;
          }
        },
        { signal },
      );
      this.addEventListener(
        "keydown",
        (event) => {
          if (event.key !== "Escape" || !(event.target instanceof Element))
            return;
          const select = event.target.closest("details[data-plp-select][open]");
          if (!select) return;
          event.preventDefault();
          event.stopPropagation();
          select.open = false;
          select.querySelector("summary")?.focus();
        },
        { signal },
      );
      this.addEventListener(
        ThemeEvents.cartUpdate,
        (event) => {
          if (
            !event.detail?.data?.didError &&
            event.detail?.data?.source === "product-form-component"
          )
            this.track("add_to_cart_success");
        },
        { signal },
      );
      this.addEventListener(
        ThemeEvents.cartError,
        () => this.track("add_to_cart_error"),
        { signal },
      );
      if (window.Shopify?.designMode) {
        document.addEventListener(
          "shopify:block:select",
          (event) => {
            if (!this.closest(".plp-card-config")?.contains(event.target))
              return;
            this.closest(".plp-collection")
              ?.querySelectorAll("plp-plan-dialog")
              .forEach((dialog) => dialog.closeDialog?.());
            this.closest(".plp-card")?.scrollIntoView({
              block: "center",
              behavior: "instant",
            });
          },
          { signal },
        );
      }
      this.update();
      this.querySelectorAll("[data-plp-enhancement], [data-plp-learn]").forEach(
        (element) => {
          element.hidden = false;
        },
      );
      this.querySelectorAll(
        "[data-plp-fallback], [data-plp-fallback-price]",
      ).forEach((element) => {
        element.hidden = true;
      });
      this.enhanced = true;
    } catch (error) {
      this.cleanup();
      this.querySelectorAll("[data-plp-enhancement], [data-plp-learn]").forEach(
        (element) => {
          element.hidden = true;
        },
      );
      this.querySelectorAll(
        "[data-plp-fallback], [data-plp-fallback-price]",
      ).forEach((element) => {
        element.hidden = false;
      });
      const message = this.querySelector("[data-plp-error]");
      if (message) {
        message.textContent = this.config?.strings?.loadError || "";
        message.hidden = false;
      }
      console.error("PLP purchase configuration could not be loaded", error);
    } finally {
      this.observeRendering();
    }
  }
  // Section morphing can preserve this element while replacing its form and
  // restoring server-rendered fallback attributes. Watch only that contract;
  // rebuilding our flavour controls must not initialize the card again.
  observeRendering() {
    this.renderObserver?.disconnect();
    const source = () =>
      [...this.querySelectorAll('script[type="application/json"]')]
        .map((node) => node.textContent)
        .join("\n");
    this.renderSource = source();
    this.renderForm = this.querySelector("form");
    this.renderObserver = new MutationObserver(() => {
      if (
        this.renderForm !== this.querySelector("form") ||
        this.renderSource !== source() ||
        (this.enhanced &&
          this.querySelector("[data-plp-fallback]")?.hidden === false)
      ) {
        this.cleanup();
        this.initialize();
      }
    });
    this.renderObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["hidden"],
    });
  }
  cleanup() {
    this.querySelector("plp-plan-dialog")?.closeDialog?.();
    this.mediaAbort?.abort();
    const image = this.closest(".plp-card")?.querySelector(".plp-card__image");
    if (image) {
      delete image.dataset.plpArtwork;
      image.hidden = false;
    }
    this.abort?.abort();
    this.pendingObserver?.disconnect();
    this.renderObserver?.disconnect();
    if (this.form?.purchaseController === this)
      delete this.form.purchaseController;
  }
  disconnectedCallback() {
    this.cleanup();
    super.disconnectedCallback();
  }
  money(amount, trimZeros = false) {
    if (!Number.isFinite(Number(amount))) return "—";
    return new Intl.NumberFormat(this.product.locale || "en", {
      style: "currency",
      currency: this.product.currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: trimZeros && Number(amount) % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(Number(amount) / 100);
  }
  selectionAvailable() {
    return this.state.selectionAvailable();
  }
  updateArtwork(offer, variant) {
    const { artwork, url: mediaURL } = artworkForOffer(
      this.config,
      offer.key,
      variant,
    );
    const image = this.closest(".plp-card").querySelector(".plp-card__image");
    if (!image || !mediaURL) return;
    this.querySelector("add-to-cart-component")?.setAttribute(
      "data-product-variant-media",
      mediaURL,
    );
    const identity = new URL(mediaURL, window.location.href);
    identity.searchParams.delete("width");
    if (image.dataset.plpArtwork === identity.href) return;
    this.mediaAbort?.abort();
    this.mediaAbort = new AbortController();
    const { signal } = this.mediaAbort;
    const matchesOffer = () => {
      const current = new URL(
        image.currentSrc || image.src,
        window.location.href,
      );
      current.searchParams.delete("width");
      return current.href === identity.href;
    };
    const reveal = () => {
      if (image.complete && image.naturalWidth > 0 && matchesOffer())
        image.hidden = false;
    };
    image.dataset.plpArtwork = identity.href;
    // Do not leave the previous offer's promotional artwork visible while loading.
    image.hidden = !matchesOffer();
    if (image.hidden) image.loading = "eager";
    image.addEventListener("load", reveal, { signal });
    image.addEventListener(
      "error",
      () => {
        image.hidden = true;
        delete image.dataset.plpArtwork;
      },
      { signal },
    );
    const imageURL = (width) => {
      const url = new URL(identity);
      url.searchParams.set("width", String(width));
      return url.href;
    };
    image.srcset = [240, 360, 480, 640, 800, 1000]
      .map((width) => `${imageURL(width)} ${width}w`)
      .join(", ");
    image.src = imageURL(1000);
    if (artwork) {
      image.width = artwork.width;
      image.height = artwork.height;
    }
    reveal();
  }
  updateGuarantee(offer) {
    const guarantee = this.querySelector("[data-plp-guarantee]");
    if (!guarantee) return;
    const guaranteeKey = this.nativeData
      ? offer.mode === "onetime"
        ? "onetime"
        : offer.days === 90
          ? "subscription_90"
          : "subscription_30"
      : offer.key;
    const type = guaranteeForOffer(this.config.guarantees, guaranteeKey);
    const content = guarantee.querySelector(
      `[data-plp-guarantee-content="${type}"]`,
    );
    const visible = type !== "none" && !!content;
    if (!visible) guarantee.querySelector(":popover-open")?.hidePopover();
    guarantee.hidden = !visible;
    guarantee
      .querySelectorAll("[data-plp-guarantee-content]")
      .forEach((body) => {
        body.hidden = body !== content;
      });
    const label =
      type === "30_day" ? this.strings.guarantee30 : this.strings.guarantee90;
    guarantee.querySelector("[data-plp-guarantee-label]").textContent = visible
      ? label
      : "";
    guarantee
      .querySelector("[popover]")
      .setAttribute("aria-label", visible ? label : "");
  }
  syncForm() {
    const variant = this.state.variant();
    const plan = this.state.allocation(this.state.selected, variant);
    this.form.elements.namedItem("id").value = variant?.id || "";
    this.form.elements.namedItem("quantity").value = "1";
    const planInput = this.form.elements.namedItem("selling_plan");
    planInput.value = plan?.id || "";
    planInput.disabled = !plan;
    this.form.dataset.purchaseAvailable = String(this.selectionAvailable());
  }
  /** @param {Event} event - Native radio change. */
  handleChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const inDialog = !!input.closest("dialog");
    if (this.form.dataset.purchasePending === "true") {
      this.update();
      return;
    }
    if (input.hasAttribute("data-plp-offer")) {
      if (this.state.select(input.value)) this.track("plan_change");
    } else if (input.hasAttribute("data-plp-flavor")) {
      this.state.setFlavor(Number(input.dataset.slot), input.value);
      this.track("flavor_change");
    } else return;
    const slot = input.dataset.slot;
    const value = input.value;
    this.update();
    if (inDialog && input.hasAttribute("data-plp-offer")) {
      this.querySelector("plp-plan-dialog")?.closeDialog();
      return;
    }
    const scope = inDialog
      ? this.querySelector("dialog")
      : this.querySelector("[data-plp-flavors]");
    const replacement =
      slot !== undefined
        ? [...scope.querySelectorAll("[data-plp-flavor]")].find(
            (node) => node.dataset.slot === slot && node.value === value,
          )
        : [...scope.querySelectorAll("[data-plp-offer]")].find(
            (node) => node.value === value,
          );
    const select = replacement?.closest("details[data-plp-select]");
    (select?.querySelector("summary") || replacement)?.focus({
      preventScroll: true,
    });
  }
  /** @param {MouseEvent} event - Native button/link click. */
  handleClick(event) {
    if (!(event.target instanceof Element)) return;
    const selectedOffer = event.target.closest("input[data-plp-offer]");
    if (selectedOffer?.value === this.state.selected.key) {
      this.querySelector("plp-plan-dialog")?.closeDialog();
      return;
    }
    const trigger = event.target.closest("[data-plp-open]");
    if (
      trigger instanceof HTMLElement &&
      this.form.dataset.purchasePending !== "true"
    ) {
      this.querySelector("plp-plan-dialog")?.showDialog?.(trigger);
      this.track("change_open");
    }
    const link = event.target.closest('[data-plp-action="learn-more"]');
    if (link) this.track("learn_more");
  }
  track(action) {
    const variant = this.state.variant();
    this.dispatchEvent(
      new CustomEvent("plp:interaction", {
        bubbles: true,
        detail: {
          experience: "quick",
          action,
          productId: this.product.id,
          variantId: variant?.id || null,
          sellingPlanId:
            this.state.allocation(this.state.selected, variant)?.id || null,
          plan: this.state.selected?.key,
          flavors: this.state.flavors().slice(),
        },
      }),
    );
  }
  put(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value;
  }
  title(offer) {
    if (!this.nativeData) return this.strings[offer.key];
    return (
      offer.title ||
      (offer.mode === "onetime"
        ? this.strings.onetime
        : offer.supply
          ? text(this.strings.supplyDays, { days: offer.days })
          : this.strings.subscribeSave)
    );
  }
  cadence(offer) {
    const configured =
      offer.days === 90 ? this.config.billing90 : this.config.billing30;
    const label =
      offer.cadence ||
      configured ||
      this.state.allocation(offer, this.state.variant(offer))?.name ||
      "";
    return label.charAt(0).toLocaleUpperCase() + label.slice(1);
  }
  nativeGiftCount(offer) {
    if (offer.mode === "onetime" || !offer.gifts) return 0;
    const allowed =
      this.nativeData?.marketOverrides?.[this.nativeData.country]?.gifts;
    return (this.config.giftProducts || []).filter(
      (id) => !allowed || allowed.includes(id),
    ).length;
  }
  detail(offer) {
    if (this.nativeData) {
      const configuredDetail =
        offer.days === 90
          ? this.config.detail90
          : offer.days === 30
            ? this.config.detail30
            : "";
      const detail =
        (this.config.overrideNativeDetails ? configuredDetail : "") ||
        offer.description ||
        configuredDetail ||
        (offer.packs === 1 && offer.days === 30
          ? this.strings.singlePack
          : text(this.strings.packs, {
              packs: offer.packs,
              servings: offer.days,
            }));
      return detail;
    }
    return (
      (offer.key === "subscription_90"
        ? this.config.detail90
        : this.config.detail30) ||
      (offer.packs === 1
        ? this.strings.singlePack
        : text(this.strings.packs, {
            packs: offer.packs,
            servings: offer.days,
          }))
    );
  }
  flavorIcon(value) {
    const flavor = String(value).toLowerCase();
    const key = flavor.includes("orange")
      ? "orange"
      : flavor.includes("citrus")
        ? "citrus"
        : flavor.includes("chocolate")
          ? "chocolate"
          : "";
    return key
      ? this.querySelector(`template[data-plp-icon="${key}"]`)?.innerHTML || ""
      : "";
  }
  nativeFlavorMarkup(offer, area) {
    const slots = this.state.slots(offer);
    if (!slots.length)
      return this.config.singleFlavorLabel
        ? `<fieldset class="plp-card__fieldset"><legend class="plp-card__label">${escapeHTML(this.config.flavorHeading || this.strings.flavor)}</legend><p class="plp-card__flavor plp-card__flavor--fixed">${this.flavorIcon(this.config.singleFlavorLabel)}${escapeHTML(this.config.singleFlavorLabel)}</p></fieldset>`
        : "";
    const selections = this.state.flavors(offer);
    const controls = this.state.controls(offer);
    const heading = this.config.flavorHeading || this.strings.flavor;
    const multiAxes = controls.length > 1;
    const fields = slots
      .map(({ control, index }, slot) => {
        const selected = selections[slot];
        const optionSettings = this.nativeData.optionSettings?.[control.name];
        const label = multiAxes
          ? optionSettings?.summaryLabel || control.label
          : slots.length > 1
            ? text(this.strings.pack, { number: slot + 1 })
            : heading;
        const free =
          !multiAxes &&
          offer.days === 90 &&
          index >= offer.packs - Number(this.config.freePacks90 || 0);
        return `<fieldset class="plp-card__fieldset plp-card__pack">${slots.length > 1 ? `<legend class="plp-card__pack-label">${escapeHTML(label)}${free ? ` <span>${escapeHTML(this.strings.free)}</span>` : ""}</legend>` : ""}<details class="plp-card__select" data-plp-select><summary aria-label="${escapeHTML(`${label}: ${selected}`)}"><span class="plp-card__select-value">${this.flavorIcon(selected)}<span>${escapeHTML(selected)}</span></span><span class="plp-card__select-chevron" aria-hidden="true"></span></summary><div class="plp-card__select-options" role="group" aria-label="${escapeHTML(label)}">${control.values
          .map((value) => {
            const next = selections.slice();
            next[slot] = value;
            const available = this.state.available(
              offer,
              this.state.findVariant(offer, next),
            );
            return `<label class="plp-card__select-option"><input type="radio" name="plp-flavor-${escapeHTML(this.form.getAttribute("id"))}-${area}-${slot}" data-plp-flavor data-slot="${slot}" value="${escapeHTML(value)}" ${selected === value ? "checked" : ""} ${available ? "" : "disabled"}>${this.flavorIcon(value)}<span>${escapeHTML(value)}</span></label>`;
          })
          .join("")}</div></details></fieldset>`;
      })
      .join("");
    const counts = new Map();
    selections.forEach((value) =>
      counts.set(value, (counts.get(value) || 0) + 1),
    );
    const note = multiAxes
      ? slots
          .map(
            ({ control }, slot) =>
              `${this.nativeData.optionSettings?.[control.name]?.summaryLabel || control.label}: ${selections[slot]}`,
          )
          .join(" · ")
      : [...counts].map(([value, count]) => `${count} ${value}`).join(" + ");
    return `<p class="plp-card__label">${escapeHTML(heading)}</p><div class="plp-card__packs" style="--plp-pack-columns:${Math.min(slots.length, 3)}">${fields}</div>${slots.length > 1 ? `<p class="plp-card__flavor-note">${escapeHTML(note)}</p>` : ""}`;
  }
  flavorMarkup(offer, area) {
    if (this.nativeData) return this.nativeFlavorMarkup(offer, area);
    const values = this.state.flavorValues(offer);
    const heading = this.config.flavorHeading || this.strings.flavor;
    if (!values.length) {
      return this.config.singleFlavorLabel
        ? `<fieldset class="plp-card__fieldset"><legend class="plp-card__label">${escapeHTML(heading)}</legend><p class="plp-card__flavor plp-card__flavor--fixed">${this.flavorIcon(this.config.singleFlavorLabel)}${escapeHTML(this.config.singleFlavorLabel)}</p></fieldset>`
        : "";
    }
    if (offer.packs > 1) {
      const controls = this.state
        .flavors(offer)
        .map((selected, slot) => {
          const label = text(this.strings.pack, { number: slot + 1 });
          const isFree =
            slot >= offer.packs - Number(this.config.freePacks90 || 0);
          return `<fieldset class="plp-card__fieldset plp-card__pack"><legend class="plp-card__pack-label">${escapeHTML(label)}${isFree ? ` <span>${escapeHTML(this.strings.free)}</span>` : ""}</legend><details class="plp-card__select" data-plp-select><summary aria-label="${escapeHTML(`${label}: ${selected}`)}"><span class="plp-card__select-value">${this.flavorIcon(selected)}<span>${escapeHTML(selected)}</span></span><span class="plp-card__select-chevron" aria-hidden="true"></span></summary><div class="plp-card__select-options" role="group" aria-label="${escapeHTML(label)}">${values
            .map((value) => {
              const next = this.state.flavors(offer).slice();
              next[slot] = value;
              const available = this.state.available(
                offer,
                this.state.findVariant(offer, next),
              );
              return `<label class="plp-card__select-option"><input type="radio" name="plp-flavor-${escapeHTML(this.form.getAttribute("id"))}-${area}-${slot}" data-plp-flavor data-slot="${slot}" value="${escapeHTML(value)}" ${selected === value ? "checked" : ""} ${available ? "" : "disabled"}>${this.flavorIcon(value)}<span>${escapeHTML(value)}</span></label>`;
            })
            .join("")}</div></details></fieldset>`;
        })
        .join("");
      const counts = new Map();
      for (const flavor of this.state.flavors(offer))
        counts.set(flavor, (counts.get(flavor) || 0) + 1);
      const mix = [...counts]
        .map(([flavor, count]) => `${count} ${flavor}`)
        .join(" + ");
      return `<p class="plp-card__label">${escapeHTML(heading)}</p><div class="plp-card__packs">${controls}</div><p class="plp-card__flavor-note">${escapeHTML(mix)}</p>`;
    }
    return this.state
      .flavors(offer)
      .map((selected, slot) => {
        const label =
          offer.packs > 1
            ? text(this.strings.pack, { number: slot + 1 })
            : heading;
        return `<fieldset class="plp-card__fieldset"><legend class="plp-card__label">${escapeHTML(label)}</legend><div class="plp-card__flavor-options">${values
          .map((value) => {
            const available = offer.variants.some(
              (variant) =>
                this.state.available(offer, variant) &&
                this.state.flavorIndex >= 0 &&
                variant.options[this.state.flavorIndex].includes(value),
            );
            return `<label class="plp-card__flavor"><input type="radio" name="plp-flavor-${escapeHTML(this.form.getAttribute("id"))}-${area}-${slot}" data-plp-flavor data-slot="${slot}" value="${escapeHTML(value)}" ${value === selected ? "checked" : ""} ${available ? "" : "disabled"}>${this.flavorIcon(value)}<span>${escapeHTML(value)}</span></label>`;
          })
          .join("")}</div></fieldset>`;
      })
      .join("");
  }
  update() {
    const offer = this.state.selected;
    const variant = this.state.variant();
    const available = this.selectionAvailable();
    const amount = this.state.price();
    const compare = this.state.compare();
    this.syncForm();
    const showCompare = compare > amount;
    this.put("[data-plp-plan-title]", this.title(offer));
    this.put(
      "[data-plp-cadence]",
      this.nativeData && offer.mode === "subscription"
        ? this.cadence(offer)
        : "",
    );
    const note = this.querySelector("[data-plp-plan-note]");
    if (note) {
      const gifts = this.nativeGiftCount(offer);
      note.textContent = gifts
        ? text(this.strings.giftCount, { count: gifts })
        : offer.popular
          ? this.strings.mostPopular
          : "";
      note.hidden = !note.textContent;
    }
    this.put("[data-plp-plan-detail]", this.detail(offer));
    this.put(
      "[data-plp-price]",
      amount === undefined ? "—" : this.money(amount),
    );
    this.put("[data-plp-compare]", this.money(compare));
    this.querySelector("[data-plp-compare]").hidden = !showCompare;
    this.put(
      "[data-plp-per-day]",
      amount === undefined
        ? ""
        : text(this.strings.perUnit, {
            price: this.money(amount / offer.days),
            unit: this.config.unitLabel || "day",
          }),
    );
    this.put(
      "[data-plp-saving]",
      showCompare
        ? text(this.strings.save, {
            percent: Math.round(((compare - amount) / compare) * 100),
          })
        : "",
    );
    this.querySelector("[data-plp-saving]").hidden = !showCompare;
    const plan = this.state.allocation(offer, variant);
    const optionValues =
      this.planOptions.find((item) => String(item.id) === String(plan?.id))
        ?.options || [];
    const interval =
      optionValues.find((item) => /deliver|frequency|billing/i.test(item.name))
        ?.value ||
      optionValues[0]?.value ||
      plan?.name;
    const billingInterval =
      offer.key === "subscription_90"
        ? this.config.billing90
        : this.config.billing30;
    const renewal = plan
      ? text(this.strings.renewal, {
          price: this.money(plan.recurringPrice ?? plan.price),
          interval: billingInterval || interval,
        })
      : "";
    this.querySelectorAll("[data-plp-renewal]").forEach((element) => {
      element.textContent = plan ? renewal : this.strings.oneTime;
      element.hidden = !plan && !!element.closest("dialog");
    });
    const includeGifts = this.nativeData
      ? !!this.nativeGiftCount(offer)
      : offer.key === "subscription_90"
        ? this.config.gifts90
        : offer.key === "subscription_30"
          ? this.config.gifts30
          : this.config.giftsOnetime;
    this.querySelectorAll("[data-plp-gifts]").forEach((element) => {
      element.hidden = !includeGifts;
    });
    this.querySelectorAll("[data-plp-competition]").forEach((element) => {
      const eligible =
        !!plan && !!includeGifts && !!this.config.competitionEnabled;
      if (!eligible) element.querySelector(":popover-open")?.hidePopover();
      element.hidden = !eligible;
    });
    const shipping = this.querySelector("[data-plp-shipping]");
    if (shipping)
      shipping.hidden = !(
        Number(this.config.freeShippingThreshold) > 0 &&
        amount >= this.config.freeShippingThreshold
      );
    const banner = this.querySelector("[data-plp-offer-banner]");
    if (banner) {
      banner.textContent = plan
        ? (offer.key === "subscription_90"
            ? this.config.offer90
            : this.config.offer30) || this.strings.subscribeSave
        : this.strings.onetime;
      banner.hidden = !banner.textContent;
    }
    this.put(
      ".add-to-cart-text__content",
      available
        ? `${this.strings.add} | ${this.money(amount)}`
        : variant?.available === false
          ? this.strings.soldOut
          : this.strings.unavailableLabel,
    );
    const submit = this.form.querySelector('[name="add"]');
    submit.disabled =
      !available || this.form.dataset.purchasePending === "true";
    this.put("[data-plp-error]", available ? "" : this.strings.unavailable);
    this.querySelector("[data-plp-error]").hidden = available;

    if (!this.nativeData) this.updateArtwork(offer, variant);
    this.updateGuarantee(offer);
    const inline = this.querySelector("[data-plp-flavors]");
    inline.innerHTML =
      this.flavorMarkup(offer, "card") +
      (this.config.flavorNote
        ? `<p class="plp-card__flavor-note">${escapeHTML(this.config.flavorNote)}</p>`
        : "");
    const isOpen = this.querySelector("dialog").open;
    this.querySelectorAll("[data-plp-open]").forEach((button) =>
      button.setAttribute("aria-expanded", String(isOpen)),
    );
    this.querySelector("[data-plp-offers]").innerHTML =
      `<legend class="visually-hidden">${escapeHTML(this.config.strings.choosePlan)}</legend>` +
      this.state.offers
        .map((item) => {
          const itemVariant = this.state.variant(item) || item.variants[0];
          const price = this.state.price(item, itemVariant);
          const reference = this.state.compare(item, itemVariant);
          const saving =
            reference > price
              ? text(this.strings.save, {
                  percent: Math.round(((reference - price) / reference) * 100),
                })
              : "";
          const isOnetime = item.mode === "onetime" || item.key === "onetime";
          const separator =
            !this.nativeData && isOnetime && this.state.offers.length > 1
              ? `<div class="plp-plan-dialog__divider" aria-hidden="true">${escapeHTML(this.strings.or)}</div>`
              : "";
          const banner =
            item.days === 90 && Number(this.config.freePacks90) > 0
              ? this.config.offer90
              : "";
          const perUnit = text(this.strings.perUnit, {
            price: this.money(price / item.days),
            unit: this.config.unitLabel || "day",
          });
          const cadence = this.nativeData
            ? isOnetime
              ? this.strings.oneDelivery
              : this.cadence(item)
            : "";
          const giftCount = this.nativeGiftCount(item);
          const nativeNote = isOnetime
            ? this.strings.oneTimeShort.toLocaleUpperCase(
                this.product.locale || "en",
              )
            : [
                saving.toLocaleUpperCase(this.product.locale || "en"),
                item.popular
                  ? this.strings.mostPopular
                  : giftCount
                    ? this.strings.freeGifts
                    : "",
              ]
                .filter(Boolean)
                .join(" · ");
          const metadata = this.nativeData
            ? `<span class="plp-plan-dialog__cadence">${escapeHTML(cadence)}</span><span class="plp-plan-dialog__native-note">${escapeHTML(nativeNote)}</span>`
            : saving && !isOnetime
              ? `<span class="plp-plan-dialog__saving">${escapeHTML(saving)}</span>`
              : "";
          return `${separator}<label class="plp-plan-dialog__offer${isOnetime ? " plp-plan-dialog__offer--onetime" : ""}">${banner ? `<span class="plp-plan-dialog__banner">${escapeHTML(banner)}</span>` : ""}<span class="plp-plan-dialog__offer-body"><input class="plp-plan-dialog__radio" type="radio" name="plp-offer-${escapeHTML(this.form.getAttribute("id"))}" data-plp-offer value="${item.key}" ${item === offer ? "checked" : ""} ${this.state.canSelect(item) ? "" : "disabled"}><span class="plp-plan-dialog__copy"><strong class="plp-plan-dialog__name">${escapeHTML(this.title(item))}</strong><span class="plp-plan-dialog__detail">${escapeHTML(this.detail(item))}</span>${metadata}</span><span class="plp-plan-dialog__prices"><span class="plp-plan-dialog__price-row">${reference > price ? `<s class="plp-plan-dialog__compare">${escapeHTML(this.money(reference, !this.nativeData))}</s>` : ""}<strong class="plp-plan-dialog__price">${escapeHTML(this.money(price, !this.nativeData))}</strong></span><span class="plp-plan-dialog__unit">${escapeHTML(perUnit)}</span></span></span></label>`;
        })
        .join("");
    this.querySelector("dialog").dataset.offerCount = String(
      this.state.offers.length,
    );
  }
}

if (!customElements.get("plp-plan-dialog"))
  customElements.define("plp-plan-dialog", PlpPlanDialog);
if (!customElements.get("plp-quick-shop"))
  customElements.define("plp-quick-shop", PlpQuickShop);
